import { useCallback, useRef } from 'react';

/**
 * WakeLock API が利用可能かチェック
 *
 * @returns
 */
export const isWakeLockSupported = (): boolean => 'wakeLock' in navigator;

/**
 * 画面の消灯、暗転、ロック用 hooks
 *
 * @returns
 */
export const useWakeLock = () => {
  // 取得した WakeLock センチネルを保持する（解放・二重取得防止に使用）
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);
  // リスナーリーク防止 - ハンドラを保持して removeEventListener で解除できるようにする
  const visibilityHandlerRef = useRef<(() => Promise<void>) | null>(null);

  /**
   * 画面の消灯、暗転、ロックを制御する
   */
  const addWakeLock = useCallback(() => {
    if (!isWakeLockSupported()) {
      return;
    }

    // 二重取得防止 - 既存ロックがある場合はスキップ
    if (wakeLockRef.current !== null) {
      return;
    }

    // NotAllowedError（ユーザー操作なしで呼んだ場合）をハンドリング
    navigator.wakeLock
      .request('screen')
      .then((sentinel: WakeLockSentinel) => {
        // sentinel が OS に解放されたら ref を null に戻す
        sentinel.addEventListener('release', () => {
          wakeLockRef.current = null;
        });
        wakeLockRef.current = sentinel;
      })
      .catch(() => {
        // NotAllowedError などは無視
      });

    // 既存リスナーを先に解除して二重登録を防ぐ
    if (visibilityHandlerRef.current) {
      document.removeEventListener(
        'visibilitychange',
        visibilityHandlerRef.current,
      );
    }

    // 復帰時の処理
    const handleVisibilityChange = async () => {
      // isWakeLockSupported ガードを追加（非対応ブラウザで TypeError が発生しないようにする）
      if (!isWakeLockSupported() || document.visibilityState !== 'visible') {
        return;
      }
      // 既存の有効な sentinel がある場合はスキップ（多重発火対策）
      if (wakeLockRef.current !== null) {
        return;
      }
      // NotAllowedError をハンドリング
      try {
        const sentinel = await navigator.wakeLock.request('screen');
        sentinel.addEventListener('release', () => {
          wakeLockRef.current = null;
        });
        wakeLockRef.current = sentinel;
      } catch {
        // NotAllowedError などは無視
      }
    };

    // ハンドラを Ref に保持
    visibilityHandlerRef.current = handleVisibilityChange;
    document.addEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  /**
   * 画面の消灯、暗転、ロックの制御を解放する
   */
  const removeWakeLock = useCallback(() => {
    if (wakeLockRef.current) {
      wakeLockRef.current.release().catch(() => {
        // release の失敗は無視
      });
      wakeLockRef.current = null;
    }
    // visibilitychange リスナーを解除
    if (visibilityHandlerRef.current) {
      document.removeEventListener(
        'visibilitychange',
        visibilityHandlerRef.current,
      );
      visibilityHandlerRef.current = null;
    }
  }, []);

  return {
    addWakeLock,
    removeWakeLock,
  };
};
